import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { activateSelfie, checkShopOrigin, fetchPreferences, fetchSelfies, getImage, photoUpload, savePreferences } from '../utils/apiService';
import { STEPS, closeAfterSignOut, nextStep, notifyShop, notifySignOut, parseConnectParams, readPreferences, readSelfies, shopName } from '../utils/connectFlow';
import AutoTryOnSwitch from '../components/AutoTryOnSwitch';

const LOGO = 'https://uploads.kuzfit.com/KuzFit-Logo.png';
const MARK = 'https://uploads.kuzfit.com/KF-Logo-Only.png';
const PRIVACY_URL = 'https://kuzfit.com/about';

/** One of the shopper's selfies, loaded with their token (the image is not public). */
const SelfieThumb = ({ fileId, size }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [src, setSrc] = useState('');

  useEffect(() => {
    let url = '';
    let live = true;
    getAccessTokenSilently()
      .then((token) => getImage(fileId, token))
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (live) setSrc(url);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId, getAccessTokenSilently]);

  const box = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', background: '#e9ecef' };
  return src ? <img src={src} alt="Your selfie" style={box} /> : <div style={box} />;
};

/** One titled block of the settings view. */
const Section = ({ title, children }) => (
  <section className="border-top pt-3 mt-3 text-left">
    <h2 className="h6 text-uppercase text-muted mb-2" style={{ letterSpacing: '0.04em', fontSize: 12 }}>{title}</h2>
    {children}
  </section>
);

/**
 * The popup a shop opens from its "Try it on" button: log in or sign up, add a selfie if there
 * is none yet, then give the shop (and only the shop whose key opened it) the shopper's access token.
 * Opened from the shop's KuzFit logo (view=settings) it is the shopper's KuzFit settings for this
 * shop: which shop it is connected to and who is signed in, their photo (replace, or switch back to
 * an earlier one), automatic try-ons, and disconnecting this shop.
 */
const Connect = () => {
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
  const { shopKey, origin, view, valid } = useMemo(() => parseConnectParams(window.location.search), []);
  const shop = shopName(origin);

  const [originChecked, setOriginChecked] = useState(!valid);
  const [originOk, setOriginOk] = useState(false);
  const [selfies, setSelfies] = useState(undefined);
  const [auto, setAuto] = useState(undefined);
  const [savingAuto, setSavingAuto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [returnLink, setReturnLink] = useState(false);
  const hasSelfie = selfies === undefined ? undefined : selfies.versions.length > 0;

  useEffect(() => {
    if (!valid) return;
    checkShopOrigin(shopKey, origin)
      .then(setOriginOk)
      .catch(() => setOriginOk(false))
      .finally(() => setOriginChecked(true));
  }, [valid, shopKey, origin]);

  const loadSelfies = useCallback(async () => {
    const found = readSelfies(await fetchSelfies(await getAccessTokenSilently()));
    setSelfies(found);
    return found;
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!originOk || !isAuthenticated) return;
    loadSelfies().catch(() => setSelfies({ active: null, versions: [] }));
    getAccessTokenSilently()
      .then(fetchPreferences)
      .then((found) => setAuto(readPreferences(found).autoTryOn))
      .catch(() => setAuto(undefined));
  }, [originOk, isAuthenticated, loadSelfies, getAccessTokenSilently]);

  const step = nextStep({ originChecked, originOk, isLoading, isAuthenticated, hasSelfie, view });

  /**
   * Gives the shop the shopper's access token, which selfie is active and, once known, whether
   * automatic try-ons are on; false if there is no shop to tell.
   */
  const tellShop = useCallback(async (active, autoTryOn) => {
    return notifyShop(window.opener, origin, await getAccessTokenSilently(), active, autoTryOn);
  }, [origin, getAccessTokenSilently]);

  useEffect(() => {
    if (step !== STEPS.READY) return;
    tellShop(selfies && selfies.active, auto)
      .then((told) => {
        if (told) {
          setTimeout(() => window.close(), 1200);
        } else {
          setReturnLink(true);
        }
      })
      .catch(() => setError('We could not reach KuzFit. Please try again.'));
    // Only once the shopper is ready, not again as selfies reload.
  }, [step]);

  const login = (screenHint) =>
    loginWithRedirect({
      appState: { returnTo: `/connect${window.location.search}` },
      authorizationParams: screenHint ? { screen_hint: screenHint } : undefined,
    });

  const uploadSelfie = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await photoUpload(file, await getAccessTokenSilently());
      const found = await loadSelfies();
      if (view === 'settings') {
        await tellShop(found.active, auto);
        setNotice('Your new selfie is in use. The shop will refresh your try-ons.');
      }
    } catch (e) {
      setError('That photo could not be uploaded. Please try another.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }, [getAccessTokenSilently, loadSelfies, tellShop, view, auto]);

  const switchSelfie = async (fileId) => {
    setError('');
    try {
      await activateSelfie(fileId, await getAccessTokenSilently());
      const found = await loadSelfies();
      await tellShop(found.active, auto);
      setNotice('Switched. The shop will refresh your try-ons.');
    } catch (e) {
      setError('We could not switch selfies. Please try again.');
    }
  };

  const changeAuto = async (on) => {
    setError('');
    setSavingAuto(true);
    try {
      const saved = readPreferences(await savePreferences(on, await getAccessTokenSilently()));
      setAuto(saved.autoTryOn);
      await tellShop(selfies && selfies.active, saved.autoTryOn);
      setNotice(saved.autoTryOn
        ? 'Saved. Shops that allow it will show products on you as you browse.'
        : 'Saved. Products show on you only when you press "Try it on".');
    } catch (e) {
      setError('We could not save that. Please try again.');
    } finally {
      setSavingAuto(false);
    }
  };

  const signOutHere = () => {
    if (notifySignOut(window.opener, origin)) {
      setNotice(`Disconnected from ${shop}.`);
      setTimeout(() => window.close(), 1200);
    } else {
      setError('Please close this window and sign out from the shop page.');
    }
  };

  /**
   * Out of KuzFit altogether: this shop is told at once, and Auth0 logs out and sends the popup
   * back to the app, which closes it. Other shops see the logout through their KuzFit frame.
   */
  const signOutOfKuzFit = async () => {
    setError('');
    notifySignOut(window.opener, origin);
    closeAfterSignOut(window);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const active = selfies && selfies.versions.find((v) => v.active);
  const earlier = selfies ? selfies.versions.filter((v) => !v.active) : [];
  const photoInput = (label, small) => (
    <label className={`btn btn-primary ${small ? 'btn-sm mb-0' : ''} ${uploading ? 'disabled' : ''}`} style={small ? undefined : { minWidth: 220 }}>
      {uploading ? 'Uploading…' : label}
      <input type="file" accept="image/jpeg,image/png" capture="user" hidden disabled={uploading} onChange={uploadSelfie} />
    </label>
  );

  if (step === STEPS.SETTINGS) {
    return (
      <div className="p-4 mx-auto" style={{ maxWidth: 420 }}>
        <div className="d-flex align-items-center">
          <img src={MARK} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} className="mr-3" />
          <div className="text-left">
            <h1 className="h5 mb-0">KuzFit</h1>
            <div className="small" style={{ color: '#268981' }}>
              <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#28a745', marginRight: 6 }} />
              Connected to {shop}
            </div>
          </div>
        </div>
        {user && user.email && <p className="small text-muted text-left mt-2 mb-0">Signed in as {user.email}</p>}

        <Section title="Your photo">
          {active ? (
            <div className="d-flex align-items-center">
              <SelfieThumb fileId={active.fileId} size={72} />
              <div className="ml-3">
                <p className="small text-muted mb-2">Used for every try-on (version {active.version}).</p>
                {photoInput('Replace photo', true)}
              </div>
            </div>
          ) : (
            <>
              <p className="small text-muted mb-2">Add a clear photo of your face to see products on you.</p>
              {photoInput('Add a photo', true)}
            </>
          )}
          {earlier.length > 0 && (
            <div className="mt-3">
              <div className="small text-muted mb-1">Earlier photos</div>
              <div className="d-flex flex-wrap">
                {earlier.map((v) => (
                  <div key={v.fileId} className="mr-3 mb-2 d-flex flex-column align-items-center">
                    <SelfieThumb fileId={v.fileId} size={44} />
                    <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => switchSelfie(v.fileId)}>
                      Use this one
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Try-ons">
          {auto === undefined
            ? <p className="small text-muted mb-0">One moment…</p>
            : <AutoTryOnSwitch checked={auto} disabled={savingAuto} onChange={changeAuto} />}
        </Section>

        <Section title="This shop">
          <p className="small text-muted mb-2">{shop} can show products on you with your KuzFit photo.</p>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={signOutHere}>
            Disconnect {shop}
          </button>
        </Section>

        <Section title="Your KuzFit account">
          <p className="small text-muted mb-2">Signing out of KuzFit puts every shop's own photos back.</p>
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={signOutOfKuzFit}>
            Sign out of KuzFit
          </button>
        </Section>

        <div className="border-top pt-3 mt-3 text-left">
          <a className="small" href={`${window.location.origin}/upload/1`} target="_blank" rel="noopener noreferrer">
            Open my KuzFit account ↗
          </a>
        </div>
        {notice && <p className="small text-success mt-3 mb-0">{notice}</p>}
        {error && <p className="small text-danger mt-3 mb-0">{error}</p>}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center p-4" style={{ minHeight: '100vh' }}>
      <img src={LOGO} alt="KuzFit" style={{ width: 180, maxWidth: '60%', height: 'auto' }} className="mb-4" />

      {step === STEPS.CHECKING && <p className="text-muted">One moment…</p>}

      {step === STEPS.INVALID && (
        <>
          <h1 className="h5">This shop isn't set up for KuzFit</h1>
          <p className="text-muted">Please close this window and let the shop know.</p>
        </>
      )}

      {step === STEPS.LOGIN && (
        <>
          {view === 'settings' ? (
            <>
              <h1 className="h4 mb-2">Connect KuzFit to {shop}</h1>
              <p className="text-muted mb-4">Log in to see products on you here, and to manage your photo and try-on settings.</p>
            </>
          ) : (
            <>
              <h1 className="h4 mb-2">See it on you</h1>
              <p className="text-muted mb-4">{shop} uses KuzFit. Log in or create a free account to try products on with your own photo.</p>
            </>
          )}
          <button type="button" className="btn btn-primary btn-lg mb-2" style={{ minWidth: 220 }} onClick={() => login()}>
            Log in
          </button>
          <button type="button" className="btn btn-outline-primary" style={{ minWidth: 220 }} onClick={() => login('signup')}>
            Create an account
          </button>
        </>
      )}

      {step === STEPS.SELFIE && (
        <>
          <h1 className="h4 mb-2">Add a selfie</h1>
          <p className="text-muted mb-4">A clear photo of your face, looking at the camera, works best.</p>
          <label className={`btn btn-primary btn-lg ${uploading ? 'disabled' : ''}`} style={{ minWidth: 220 }}>
            {uploading ? 'Uploading…' : 'Take or choose a photo'}
            <input type="file" accept="image/jpeg,image/png" capture="user" hidden disabled={uploading} onChange={uploadSelfie} />
          </label>
          <p className="small text-muted mt-3" style={{ maxWidth: 360 }}>
            Your photo is used to show you products in KuzFit-enabled shops.{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy</a>
          </p>
        </>
      )}

      {step === STEPS.READY && !returnLink && !error && (
        <>
          <h1 className="h4 mb-2">You're all set</h1>
          <p className="text-muted">Taking you back to the shop…</p>
        </>
      )}

      {returnLink && (
        <>
          <h1 className="h4 mb-2">You're all set</h1>
          <a className="btn btn-primary btn-lg" href={origin}>Return to the shop</a>
        </>
      )}

      {error && <p className="text-danger mt-3">{error}</p>}
    </div>
  );
};

export default Connect;
